import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { transition1 } from '../transitions';
import planen from '../assets/img/planen Youka.png';
import nachhaltig from '../assets/img/nachhaltig.jpg';
import ideen from '../assets/img/ideen.jpg';

import {
  FaFacebookF,
  FaInstagram,
  FaTiktok,
  FaWhatsapp,
  FaYoutube,
  FaStar,
} from 'react-icons/fa6';
import { SiGooglemybusiness } from 'react-icons/si';

// ✅ SEO Helmet
import { Helmet } from "react-helmet-async";

const Home = () => {
  return (
    <div className="relative flex flex-col min-h-screen bg-white">

      {/* ✅ SEO META + JSON-LD structured data */}
      <Helmet>
        {/* ===== SEO META TAGS ===== */}
        <title>Youka GmbH – Bauarbeiten, Sanierung & Renovierung in Berlin</title>

        <meta
          name="description"
          content="Youka GmbH – Ihr Partner für hochwertige Bauarbeiten, Sanierungen und Renovierungen in Berlin. Präzision, Qualität und termingerechte Umsetzung."
        />

        <meta
          name="keywords"
          content="Bauarbeiten Berlin, Sanierung Berlin, Renovierung Berlin, Bauunternehmen Berlin, Bauprojekt, Youka GmbH"
        />

        <meta property="og:title" content="Youka GmbH – Bauunternehmen in Berlin" />
        <meta property="og:description" content="Ihr Partner für Bauarbeiten, Sanierungen und Renovierungen in Berlin." />
        <meta property="og:image" content="https://www.youka-gmbh.com/logoYouka.png" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.youka-gmbh.com/" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Youka GmbH – Bauunternehmen in Berlin" />
        <meta name="twitter:description" content="Sanierungen, Renovierungen und hochwertige Bauarbeiten in Berlin." />
        <meta name="twitter:image" content="https://www.youka-gmbh.com/logo.png" />

        <link rel="canonical" href="https://www.youka-gmbh.com/" />

        {/* ✅ JSON-LD STRUCTURED DATA */}
        <script type="application/ld+json">
          {`
          {
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Youka GmbH",
            "legalName": "Youka GmbH",
            "image": "https://www.youka-gmbh.com/logo.png",
            "url": "https://www.youka-gmbh.com/",
            "telephone": "+49 30 85968082",
            "email": "info@youka-gmbh.com",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Rungestraße 13",
              "addressLocality": "Berlin",
              "postalCode": "10179",
              "addressCountry": "DE"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": 52.511,
              "longitude": 13.422
            },
            "sameAs": [
              "https://www.facebook.com/youkagmbh",
              "https://www.instagram.com/youkagmbh",
              "https://www.tiktok.com/@youkagmbh",
              "https://www.youtube.com/@youkagmbh",
              "https://www.google.com/maps/place/?q=place_id:ChIJscuryPlYPAwRUrn4oiEFTMw"
            ]
          }
          `}
        </script>
      </Helmet>
      {/* ✅ END SEO */}

      {/* ===== بقية الصفحة كما هي ===== */}
      {/* Hero Section */}
      <section className="flex-grow pt-40 lg:pt-24 lg:mt-20">
        <div className="container relative mx-auto h-full px-4">
          <motion.div
            initial={{ opacity: 0, y: '100%' }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: '100%' }}
            transition={transition1}
            className="flex flex-col h-full items-center justify-center text-center text-[#103d5b]"
          >
            <div className="max-w-4xl mx-auto">
              <h1 className="h1 mb-6 text-4xl md:text-5xl font-bold">
                Willkommen bei der YOUKA GmbH
              </h1>
              <p className="text-lg leading-relaxed mb-6">
                Die <strong>YOUKA GmbH</strong> ist Ihr zuverlässiger Partner für hochwertige Bauarbeiten,
                Sanierungen und Renovierungen in Berlin und Umgebung.
                <br />
                Mit langjähriger Erfahrung, einem engagierten Team und modernen Techniken
                realisieren wir Bauprojekte mit höchster Präzision, Qualität und Stil.
                <br />
                Unser Ziel ist es, Ihre Ideen in dauerhafte Werte zu verwandeln –
                zuverlässig, professionell und termingerecht.
              </p>

              <Link
                to="./Kontakt"
                className="btnHome mb-[30px] mt-6 mx-auto lg:mx-0 self-start"
              >
                Kontakt aufnehmen
              </Link>

              {/* Social media */}
              <div className="mt-10 text-center">
                <h2 className="text-2xl md:text-3xl font-semibold mb-3">
                  Wir freuen uns auf Ihre Anfrage!
                </h2>

                <div className="flex flex-wrap justify-center gap-6 mt-6">
                  <a href="https://www.facebook.com/youkagmbh" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="text-[#103d5b] hover:text-blue-600 text-3xl transition-colors"><FaFacebookF /></a>
                  <a href="https://www.instagram.com/youkagmbh" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="text-[#103d5b] hover:text-pink-600 text-3xl transition-colors"><FaInstagram /></a>
                  <a href="https://www.tiktok.com/@youkagmbh" target="_blank" rel="noopener noreferrer" aria-label="TikTok" className="text-[#103d5b] hover:text-black text-3xl transition-colors"><FaTiktok /></a>
                  <a href="https://wa.me/491604035965" target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className="text-[#103d5b] hover:text-green-600 text-3xl transition-colors"><FaWhatsapp /></a>
                  <a href="https://www.youtube.com/@youkagmbh" target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="text-[#103d5b] hover:text-red-600 text-3xl transition-colors"><FaYoutube /></a>
                  <a href="https://share.google/mOe0aR4AdyHDlkLxr" target="_blank" rel="noopener noreferrer" aria-label="Google Business" className="text-[#103d5b] hover:text-blue-400 text-3xl transition-colors"><SiGooglemybusiness /></a>
                  <a href="https://search.google.com/local/writereview?placeid=ChIJscuryPlYPAwRUrn4oiEFTMw" target="_blank" rel="noopener noreferrer" aria-label="Google Bewertung" className="text-[#103d5b] hover:text-yellow-500 text-3xl transition-colors"><FaStar /></a>
                </div>
              </div>
            </div>

            <div className="w-full mt-16 flex justify-center">
              <div className="w-full max-w-5xl aspect-video rounded-2xl overflow-hidden shadow-2xl border border-gray-800">
                <iframe
                  className="w-full h-full"
                  src="https://www.youtube.com/embed/xX6ukRdJQIU?&rel=0"
                  title="YOUKA GmbH Präsentation"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 text-[#103d5b]">

          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Unsere Expertise & Philosophie</h2>
            <p className="max-w-3xl mx-auto text-lg leading-relaxed">
              Qualität, Präzision und Vertrauen stehen bei der YOUKA GmbH im Mittelpunkt.
              Unser Unternehmen steht für professionelles Handwerk, innovative Lösungen
              und nachhaltige Baukonzepte, die den höchsten Ansprüchen gerecht werden.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            <img src={planen} alt="Bauarbeiten" className="rounded-2xl shadow-2xl w-full object-cover" />
            <div>
              <h3 className="text-3xl font-semibold mb-4">Präzision im Handwerk</h3>
              <p className="text-lg leading-relaxed">
                Wir legen Wert auf jedes Detail. Unser erfahrenes Team sorgt für perfekte Ausführung –
                von der Planung bis zur Fertigstellung. Jedes Projekt ist ein Unikat.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center mb-16 md:flex-row-reverse">
            <div className="order-2 md:order-1">
              <h3 className="text-3xl font-semibold mb-4">Nachhaltiges Bauen</h3>
              <p className="text-lg leading-relaxed">
                Wir setzen auf nachhaltige Materialien und energieeffiziente Prozesse.
                Unser Ziel ist es, langlebige Gebäude zu schaffen, die Umwelt schonen.
              </p>
            </div>
            <img src={nachhaltig} alt="Nachhaltiges Bauen" className="rounded-2xl shadow-2xl w-full object-cover order-1 md:order-2" />
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <img src={ideen} alt="Projektbetreuung" className="rounded-2xl shadow-2xl w-full object-cover" />
            <div>
              <h3 className="text-3xl font-semibold mb-4">Individuelle Projektbetreuung</h3>
              <p className="text-lg leading-relaxed">
                Wir begleiten Sie von der ersten Idee bis zur finalen Umsetzung.
                Transparente Kommunikation und persönliche Beratung sind selbstverständlich.
              </p>
            </div>
          </div>

        </div>
      </section>
    </div>
  );
};

export default Home;
